import { TCreateJobReqDto, TCreateJobResDto } from "@/api/services/main/job/dtos/create-job-req.dto";
import { TGetJobListReqDto, TGetJobListResDto } from "@/api/services/main/job/dtos/get-job-list.dto";
import { TGetJobResDto } from "@/api/services/main/job/dtos/get-job.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useMemo } from "react";

const processDataBeforeCallApi = (payload: TCreateJobReqDto) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return formData.append(key, "");
    if (Array.isArray(value) && value.length === 0) return formData.append(key, "");

    if (value instanceof File) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      if (value.every((v) => v instanceof File)) {
        value.forEach((file) => formData.append(key, file));
      } else {
        formData.append(key, JSON.stringify(value));
      }
    } else if (typeof value === "number") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
  return formData;
};

export const createJobApi = async (payload: TCreateJobReqDto): Promise<TCreateJobResDto> => {
  const formData = processDataBeforeCallApi(payload);
  const rs = await clientApi.post<TCreateJobResDto>("/jobs", formData);
  return rs.data;
};

export const getJobListApi = async (params: TGetJobListReqDto): Promise<TGetJobListResDto> => {
  const { data } = await clientApi.get<TGetJobListResDto>("/jobs", { params });
  return data;
};

export const useJobList = (params: TGetJobListReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.JOB.FETCH_LIST, params],
    queryFn: () => getJobListApi(params),
  });
};

export const fetchJobInfoApi = async (id: string | number): Promise<TGetJobResDto> => {
  const { data } = await clientApi.get<TGetJobResDto>(`/jobs/${id}`);
  return data;
};

export const useJobInfo = (id?: number) => {
  const { jobId } = useParams();

  const fetchId = id ?? jobId;

  const isValidId = useMemo(() => {
    if (!fetchId) return false;
    const idNumber = Number(fetchId);
    return !isNaN(idNumber) && Number.isInteger(idNumber) && idNumber > 0;
  }, [fetchId]);

  return useQuery({
    queryKey: [QUERY_KEYS.JOB.FETCH_DETAIL, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchJobInfoApi(fetchId) : undefined;
    },
    enabled: isValidId,
  });
};

export const updateJobApi = async (id: string | number, payload: TCreateJobReqDto): Promise<TCreateJobResDto> => {
  const formData = processDataBeforeCallApi(payload);
  const rs = await clientApi.put<TCreateJobResDto>(`/jobs/${id}`, formData);
  return rs.data;
};
